import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import LazyImage from './LazyImage';
import './UnifiedGallery.css';

/**
 * UnifiedGallery - Standardized gallery component with consistent features across all gallery types
 * 
 * This component provides a unified gallery experience with identical features:
 * - Slideshow controls with speed adjustment
 * - Thumbnail navigation
 * - Full-screen viewing with zoom capabilities
 * - Consistent navigation and viewing options
 * - Metadata display and captions
 * - Responsive design for all screen sizes
 */
const UnifiedGallery = forwardRef(({ 
  images = [], 
  title = '', 
  type = 'default',
  features = {
    slideshow: true,
    thumbnails: true,
    fullscreen: true,
    zoom: true,
    captions: true,
    sharing: false
  },
  layout = {
    thumbnailSize: 220,
    maxDisplay: null,
    columns: 'auto',
    spacing: 'default'
  },
  className = '',
  onImageClick,
  onGalleryOpen,
  onGalleryClose
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [showAll, setShowAll] = useState(false);
  const [isSlideshow, setIsSlideshow] = useState(false);
  const [slideshowInterval, setSlideshowInterval] = useState(null);
  const [slideshowSpeed, setSlideshowSpeed] = useState(3000);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Mobile detection state
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia('(max-width: 768px)');
      return mql ? mql.matches : false;
    }
    return false;
  });

  // Touch gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [pinchStartDistance, setPinchStartDistance] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isPinching, setIsPinching] = useState(false);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    openGallery: (index = 0) => {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
      setCurrentIndex(index);
      setIsOpen(true);
      onGalleryOpen?.(index);
    },
    closeGallery: () => {
      setIsOpen(false);
      stopSlideshow();
      resetZoom();
      onGalleryClose?.();
    },
    nextImage,
    prevImage,
    goToImage
  }));

  // Close gallery on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeGallery();
      }
    };

    const handleKeyNavigation = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextImage();
          break;
        case ' ':
          e.preventDefault();
          toggleSlideshow();
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleKeyNavigation);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleKeyNavigation);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Mobile viewport detection with resize listener
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    if (!mediaQuery) return;

    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const openGallery = (index) => {
    // Synchronously scroll to top and lock body overflow before opening lightbox
    // This ensures the fixed overlay is immediately visible regardless of scroll position
    window.scrollTo(0, 0);
    document.body.style.overflow = 'hidden';
    setCurrentIndex(index);
    setIsOpen(true);
    resetZoom();
    onImageClick?.(images[index], index);
    onGalleryOpen?.(index);
  };

  const closeGallery = () => {
    setIsOpen(false);
    stopSlideshow();
    resetZoom();
    onGalleryClose?.();
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    resetZoom();
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    resetZoom();
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
    resetZoom();
  };

  // Zoom functionality
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.5, 5));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.5, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // Mouse drag functionality for zoomed images
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch gesture handlers for mobile swipe, pinch-to-zoom, and pan
  const getDistanceBetweenTouches = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();

    if (e.touches.length === 2) {
      // Two-finger pinch start
      const distance = getDistanceBetweenTouches(e.touches);
      setPinchStartDistance(distance);
      setIsPinching(true);
      setIsSwiping(false);
    } else if (e.touches.length === 1) {
      // Single finger — record start position and timestamp
      setTouchStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      });
      setTouchEnd(null);
      setIsSwiping(true);
      setIsPinching(false);
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();

    if (e.touches.length === 2 && isPinching && pinchStartDistance) {
      // Two-finger pinch: adjust zoom proportionally
      const currentDistance = getDistanceBetweenTouches(e.touches);
      const ratio = currentDistance / pinchStartDistance;
      setZoomLevel(prev => {
        const newZoom = prev * ratio;
        return Math.min(Math.max(newZoom, 0.5), 5);
      });
      setPinchStartDistance(currentDistance);
    } else if (e.touches.length === 1 && isSwiping) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;

      setTouchEnd({ x: currentX, y: currentY });

      // If zoomed, pan image following touch delta
      if (zoomLevel > 1 && touchStart) {
        const deltaX = currentX - touchStart.x;
        const deltaY = currentY - touchStart.y;
        setImagePosition(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));
        // Update touchStart to current position for continuous panning
        setTouchStart({
          x: currentX,
          y: currentY,
          time: touchStart.time
        });
      }
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();

    if (isPinching) {
      // Pinch ended — reset pinch state
      setIsPinching(false);
      setPinchStartDistance(null);
    } else if (isSwiping && touchStart) {
      const endX = touchEnd ? touchEnd.x : touchStart.x;
      const horizontalDistance = endX - touchStart.x;

      if (zoomLevel <= 1) {
        // Not zoomed: check for swipe navigation
        if (Math.abs(horizontalDistance) > 50) {
          if (horizontalDistance < 0) {
            // Swipe left → next image
            nextImage();
          } else {
            // Swipe right → previous image
            prevImage();
          }
        }
      }
      // If zoomed and below threshold, treat as pan end (no action needed, position already updated)
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
    setPinchStartDistance(null);
    setIsPinching(false);
  };

  // Slideshow functionality
  const startSlideshow = () => {
    if (images.length <= 1) return;
    
    setIsSlideshow(true);
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      resetZoom();
    }, slideshowSpeed);
    setSlideshowInterval(interval);
  };

  const stopSlideshow = () => {
    setIsSlideshow(false);
    if (slideshowInterval) {
      clearInterval(slideshowInterval);
      setSlideshowInterval(null);
    }
  };

  const toggleSlideshow = () => {
    if (isSlideshow) {
      stopSlideshow();
    } else {
      startSlideshow();
    }
  };

  const changeSlideshowSpeed = (speed) => {
    setSlideshowSpeed(speed);
    if (isSlideshow) {
      stopSlideshow();
      setTimeout(() => startSlideshow(), 100);
    }
  };

  // Preload adjacent images when lightbox opens
  const preloadImage = useCallback((index) => {
    if (preloadedImages.has(index) || !images[index]) return;
    
    const img = new Image();
    img.src = images[index].optimizedSrc || images[index].src;
    img.onload = () => {
      setPreloadedImages(prev => new Set([...prev, index]));
    };
  }, [images, preloadedImages]);

  // Preload current and adjacent images
  useEffect(() => {
    if (isOpen) {
      preloadImage(currentIndex);
      preloadImage(currentIndex - 1);
      preloadImage(currentIndex + 1);
    }
  }, [isOpen, currentIndex, preloadImage]);

  // Cleanup slideshow on unmount
  useEffect(() => {
    return () => {
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
      }
    };
  }, [slideshowInterval]);

  // Reset zoom when image changes
  useEffect(() => {
    resetZoom();
  }, [currentIndex]);

  if (!images || images.length === 0) {
    return (
      <div className={`unified-gallery-container ${className}`}>
        <div className="gallery-header">
          {title && <h3 className="gallery-title">{title}</h3>}
        </div>
        <div className="no-images-message">
          <p>No images available for this gallery.</p>
        </div>
      </div>
    );
  }

  // Determine which images to display in the grid
  const hasMaxDisplay = !!(layout.maxDisplay && layout.maxDisplay > 0 && layout.maxDisplay < images.length);
  const displayedImages = hasMaxDisplay && !showAll ? images.slice(0, layout.maxDisplay) : images;
  const hiddenCount = hasMaxDisplay && !showAll ? images.length - layout.maxDisplay : 0;

  // Create custom grid style for thumbnail sizing
  const gridStyle = {
    gridTemplateColumns: layout.columns === 'auto' 
      ? (displayedImages.length === 1 
          ? `${layout.thumbnailSize}px` 
          : `repeat(auto-fill, minmax(${layout.thumbnailSize}px, 1fr))`)
      : `repeat(${layout.columns}, 1fr)`,
    justifyContent: 'center',
    gap: layout.spacing === 'large' ? '20px' : layout.spacing === 'small' ? '8px' : '12px'
  };

  const toggleShowAll = () => {
    const newShowAll = !showAll;
    setShowAll(newShowAll);
    
    // If showing more, scroll to ensure new images are visible
    if (newShowAll) {
      setTimeout(() => {
        const galleryContainer = document.querySelector('.unified-gallery-container');
        if (galleryContainer) {
          const rect = galleryContainer.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetPosition = rect.top + scrollTop - 100;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 200);
    }
  };

  const handleImageError = (index) => {
    console.warn(`Failed to load image at index ${index}:`, images[index]);
  };

  return (
    <>
      {/* Gallery Thumbnails */}
      <div className={`unified-gallery-container ${className}`}>
        <div className="gallery-header">
          {title && <h3 className="gallery-title">{title}</h3>}
          {features.slideshow && images.length > 1 && (
            <button 
              className="gallery-slideshow-btn"
              onClick={() => {
                openGallery(0);
                startSlideshow();
              }}
              title="Start Slideshow"
            >
              <i className="fas fa-play"></i>
              Slideshow
            </button>
          )}
        </div>
        
        <div className="gallery-grid" style={gridStyle}>
          {displayedImages.map((image, index) => (
            <div
              key={index}
              className="gallery-thumbnail"
              onClick={() => openGallery(index)}
            >
              <LazyImage
                src={image.thumbnailSrc || image.src}
                alt={image.alt || `${title} ${index + 1}`}
                className="gallery-thumbnail-image"
                isGalleryImage={true}
                rootMargin={300}
                eager={index < 6} // Load first 6 images immediately
                onError={(e) => {
                  console.log(`Thumbnail failed for ${image.thumbnailSrc || image.src}, trying fallbacks...`);
                  // Try optimized version first, then original
                  if (e.target.src === (image.thumbnailSrc || image.src)) {
                    e.target.src = image.optimizedSrc || image.fallbackSrc || image.src;
                  } else if (e.target.src === (image.optimizedSrc || image.fallbackSrc)) {
                    e.target.src = image.src;
                  }
                  handleImageError(index);
                }}
              />
              <div className="gallery-overlay">
                <i className="fas fa-search-plus"></i>
              </div>
            </div>
          ))}
        </div>
        
        {/* Show More/Less Button */}
        {hasMaxDisplay && (
          <div className="gallery-show-more">
            <button className="gallery-show-more-btn" onClick={toggleShowAll}>
              {showAll ? (
                <>
                  <i className="fas fa-chevron-up"></i>
                  Show Less
                </>
              ) : (
                <>
                  <i className="fas fa-chevron-down"></i>
                  Show {hiddenCount} More Photo{hiddenCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {isOpen && features.fullscreen && ReactDOM.createPortal(
        <div className="lightbox-overlay" onClick={closeGallery}>
          <div 
            className="lightbox-container" 
            onClick={(e) => e.stopPropagation()}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Close Button */}
            <button className="lightbox-close" onClick={closeGallery}>
              <i className="fas fa-times"></i>
            </button>

            {/* Zoom Controls */}
            {features.zoom && (
              <div className="lightbox-zoom-controls">
                <button 
                  className="zoom-btn"
                  onClick={zoomOut}
                  disabled={zoomLevel <= 0.5}
                  title="Zoom Out (-)"
                >
                  <i className="fas fa-search-minus"></i>
                </button>
                <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                <button 
                  className="zoom-btn"
                  onClick={zoomIn}
                  disabled={zoomLevel >= 5}
                  title="Zoom In (+)"
                >
                  <i className="fas fa-search-plus"></i>
                </button>
                <button 
                  className="zoom-btn"
                  onClick={resetZoom}
                  title="Reset Zoom (0)"
                >
                  <i className="fas fa-expand-arrows-alt"></i>
                </button>
              </div>
            )}

            {/* Slideshow Controls */}
            {features.slideshow && images.length > 1 && (
              <>
                <div className="lightbox-slideshow-controls">
                  <button 
                    className={`slideshow-toggle ${isSlideshow ? 'active' : ''}`}
                    onClick={toggleSlideshow}
                    title={isSlideshow ? 'Stop Slideshow (Space)' : 'Start Slideshow (Space)'}
                  >
                    <i className={`fas ${isSlideshow ? 'fa-pause' : 'fa-play'}`}></i>
                  </button>
                  
                  {/* Speed controls - hidden on mobile, keep only play/pause */}
                  {!isMobile && (
                    <div className="slideshow-speed-controls">
                      <button 
                        className={`speed-btn ${slideshowSpeed === 2000 ? 'active' : ''}`}
                        onClick={() => changeSlideshowSpeed(2000)}
                        title="Fast (2s)"
                      >
                        2s
                      </button>
                      <button 
                        className={`speed-btn ${slideshowSpeed === 3000 ? 'active' : ''}`}
                        onClick={() => changeSlideshowSpeed(3000)}
                        title="Normal (3s)"
                      >
                        3s
                      </button>
                      <button 
                        className={`speed-btn ${slideshowSpeed === 5000 ? 'active' : ''}`}
                        onClick={() => changeSlideshowSpeed(5000)}
                        title="Slow (5s)"
                      >
                        5s
                      </button>
                    </div>
                  )}
                </div>

                {/* Slideshow Status Indicator */}
                {isSlideshow && (
                  <div className="slideshow-status">
                    <i className="fas fa-play-circle"></i>
                    Slideshow Active
                  </div>
                )}
              </>
            )}

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button className="lightbox-nav lightbox-prev" onClick={prevImage}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button className="lightbox-nav lightbox-next" onClick={nextImage}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </>
            )}

            {/* Main Image */}
            <div className="lightbox-image-container">
              <LazyImage
                key={currentIndex}
                src={images[currentIndex].optimizedSrc || images[currentIndex].fallbackSrc || images[currentIndex].src}
                alt={images[currentIndex].alt || `${title} ${currentIndex + 1}`}
                className="lightbox-image"
                eager={true} // Always load lightbox images immediately
                style={{
                  transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                  cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onError={(e) => {
                  console.log(`Lightbox image failed, trying fallback...`);
                  const currentImage = images[currentIndex];
                  if (e.target.src === (currentImage.optimizedSrc || currentImage.fallbackSrc)) {
                    e.target.src = currentImage.src;
                  } else if (e.target.src === currentImage.src) {
                    e.target.src = currentImage.optimizedSrc || currentImage.fallbackSrc || currentImage.src;
                  }
                  handleImageError(currentIndex);
                }}
                draggable={false}
              />
              {features.captions && images[currentIndex].caption && (
                <div className="lightbox-caption">
                  {images[currentIndex].caption}
                </div>
              )}
            </div>

            {/* Thumbnail Navigation - Improved for large galleries */}
            {features.thumbnails && images.length > 1 && (
              <div className="lightbox-thumbnails">
                <div className="lightbox-thumbnails-container">
                  {/* Show only a subset of thumbnails around current image */}
                  {(() => {
                    const maxThumbnails = 10; // Show max 10 thumbnails at a time
                    const halfRange = Math.floor(maxThumbnails / 2);
                    let startIndex = Math.max(0, currentIndex - halfRange);
                    let endIndex = Math.min(images.length - 1, startIndex + maxThumbnails - 1);
                    
                    // Adjust start if we're near the end
                    if (endIndex - startIndex < maxThumbnails - 1) {
                      startIndex = Math.max(0, endIndex - maxThumbnails + 1);
                    }
                    
                    const visibleImages = images.slice(startIndex, endIndex + 1);
                    
                    return (
                      <>
                        {/* Navigation arrows for thumbnails */}
                        {startIndex > 0 && (
                          <button 
                            className="lightbox-thumb-nav lightbox-thumb-prev"
                            onClick={() => {
                              const newIndex = Math.max(0, currentIndex - maxThumbnails);
                              goToImage(newIndex);
                            }}
                            title="Previous thumbnails"
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        )}
                        
                        <div className="lightbox-thumbnails-grid">
                          {visibleImages.map((image, relativeIndex) => {
                            const actualIndex = startIndex + relativeIndex;
                            return (
                              <div
                                key={actualIndex}
                                className={`lightbox-thumb ${actualIndex === currentIndex ? 'active' : ''}`}
                                onClick={() => goToImage(actualIndex)}
                              >
                                <LazyImage
                                  src={image.thumbnailSrc || image.src}
                                  alt={image.alt || `${title} ${actualIndex + 1}`}
                                  className="lightbox-thumb-image"
                                  eager={Math.abs(actualIndex - currentIndex) <= 2}
                                  isGalleryImage={true}
                                  rootMargin={100}
                                  onError={(e) => {
                                    console.log(`Lightbox thumbnail failed, trying fallback...`);
                                    if (e.target.src === (image.thumbnailSrc || image.src)) {
                                      e.target.src = image.optimizedSrc || image.fallbackSrc || image.src;
                                    }
                                    handleImageError(actualIndex);
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Navigation arrows for thumbnails */}
                        {endIndex < images.length - 1 && (
                          <button 
                            className="lightbox-thumb-nav lightbox-thumb-next"
                            onClick={() => {
                              const newIndex = Math.min(images.length - 1, currentIndex + maxThumbnails);
                              goToImage(newIndex);
                            }}
                            title="Next thumbnails"
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
                
                {/* Thumbnail position indicator for large galleries */}
                {images.length > 20 && (
                  <div className="lightbox-thumb-indicator">
                    <div className="lightbox-thumb-progress">
                      <div 
                        className="lightbox-thumb-progress-bar"
                        style={{ width: `${((currentIndex + 1) / images.length) * 100}%` }}
                      ></div>
                    </div>
                    <span className="lightbox-thumb-position">
                      {currentIndex + 1} of {images.length}
                    </span>
                    
                    {/* Quick jump for very large galleries */}
                    {images.length > 50 && (
                      <div className="lightbox-quick-jump">
                        <input
                          type="number"
                          min="1"
                          max={images.length}
                          value={currentIndex + 1}
                          onChange={(e) => {
                            const newIndex = parseInt(e.target.value) - 1;
                            if (newIndex >= 0 && newIndex < images.length) {
                              goToImage(newIndex);
                            }
                          }}
                          className="lightbox-jump-input"
                          title="Jump to image number"
                        />
                        <button
                          onClick={() => {
                            const input = document.querySelector('.lightbox-jump-input');
                            const newIndex = parseInt(input.value) - 1;
                            if (newIndex >= 0 && newIndex < images.length) {
                              goToImage(newIndex);
                            }
                          }}
                          className="lightbox-jump-btn"
                          title="Go to image"
                        >
                          <i className="fas fa-arrow-right"></i>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Image Counter */}
            <div className="lightbox-counter">
              {currentIndex + 1} / {images.length}
            </div>

            {/* Keyboard shortcuts help - hidden on mobile (not useful on touch devices) */}
            {!isMobile && (
              <div className="lightbox-help">
                <div className="help-shortcuts">
                  <span>← → Navigate</span>
                  <span>Space Slideshow</span>
                  <span>+/- Zoom</span>
                  <span>Esc Close</span>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

UnifiedGallery.displayName = 'UnifiedGallery';

UnifiedGallery.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    caption: PropTypes.string,
    thumbnailSrc: PropTypes.string,
    optimizedSrc: PropTypes.string
  })).isRequired,
  title: PropTypes.string,
  type: PropTypes.string,
  features: PropTypes.shape({
    slideshow: PropTypes.bool,
    thumbnails: PropTypes.bool,
    fullscreen: PropTypes.bool,
    zoom: PropTypes.bool,
    captions: PropTypes.bool,
    sharing: PropTypes.bool
  }),
  layout: PropTypes.shape({
    thumbnailSize: PropTypes.number,
    maxDisplay: PropTypes.number,
    columns: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    spacing: PropTypes.oneOf(['small', 'default', 'large'])
  }),
  className: PropTypes.string,
  onImageClick: PropTypes.func,
  onGalleryOpen: PropTypes.func,
  onGalleryClose: PropTypes.func
};

export default UnifiedGallery;